import React, { FC } from 'react'
import {
  Modal,
  Box,
  Typography,
  IconButton,
  useTheme,
  Stack,
  Button,
  Divider,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import Markdown from 'markdown-to-jsx'

interface HelpContentModalProps {
  open: boolean
  onClose: () => void
  title: string
  markdownContent: string
  onBack?: () => void
}

export const HelpContentModal: FC<HelpContentModalProps> = ({
  open,
  onClose,
  title,
  markdownContent,
  onBack,
}) => {
  const theme = useTheme()

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 600,
          maxHeight: '80vh',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', top: 8, right: 8 }}
        >
          <CloseIcon />
        </IconButton>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 'bold',
            color: theme.palette.text.primary,
            mb: 3,
            flexShrink: 0,
          }}
        >
          {title}
        </Typography>
        <Divider sx={{ mb: 3, flexShrink: 0 }} />

        <Box
          sx={[
            {
              flexGrow: 1,
              overflow: 'auto',
            },
            onBack
              ? {
                  mb: 2,
                }
              : {
                  mb: 0,
                },
          ]}
        >
          <Stack spacing={3}>
            <Box
              sx={{
                '& h3': {
                  fontSize: '1.3rem',
                  fontWeight: 700,
                  mt: 3,
                  mb: 2,
                  color: '#000000',
                },
                '& h4': {
                  fontSize: '1.15rem',
                  fontWeight: 600,
                  mt: 2.5,
                  mb: 1.5,
                  color: '#000000',
                },
                '& p': {
                  lineHeight: 1.8,
                  mb: 2,
                  color: '#000000',
                  fontSize: '1.05rem',
                },
                '& strong': {
                  fontWeight: 700,
                  color: '#000000',
                },
                '& ul': {
                  pl: 3,
                },
                '& code': {
                  backgroundColor: '#f5f5f5',
                  padding: '2px 6px',
                  borderRadius: '3px',
                  fontSize: '0.9em',
                  fontFamily: 'monospace',
                  color: '#1976d2',
                },
              }}
            >
              <Markdown>{markdownContent}</Markdown>
            </Box>
          </Stack>
        </Box>

        {onBack && (
          <Stack
            direction="row"
            spacing={2}
            justifyContent="flex-end"
            sx={{ mt: 3, flexShrink: 0 }}
          >
            <Button
              variant="outlined"
              onClick={onBack}
              sx={{
                borderWidth: 2,
                borderColor: theme.palette.divider,
                color: theme.palette.text.primary,
                '&:hover': {
                  borderWidth: 2,
                  borderColor: theme.palette.grey[600],
                },
              }}
            >
              よくある質問に戻る
            </Button>
          </Stack>
        )}
      </Box>
    </Modal>
  )
}
